import { Course } from '@/services/golfClubService';
import { IonBackButton, IonButton, IonButtons, IonContent, IonFab, IonHeader, IonIcon, IonList, IonSearchbar, IonSegment, IonSegmentButton, IonTitle, IonToolbar } from '@ionic/react';
import { arrowBackOutline, listOutline, mapOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import FacilityListItem from '@/components/search/FacilityListItem';
import ClubSearchMap from '@/components/search/ClubSearchMap';

export interface SearchUIProps {
  showMap: boolean;
  setShowMap: (show: boolean) => void;
  searchRef: React.RefObject<HTMLIonSearchbarElement>;
  inputValue: string;
  setInputValue: (value: string) => void;
  segment: 'nearest' | 'playedCourses' | 'favorites';
  handleSegmentChange: (value: 'nearest' | 'playedCourses' | 'favorites') => void;
  isSearching: boolean;
  courses: Course[];
  playedCourses: Course[];
  favoriteCourses: Course[];
  handleSelectCourse: (course: Course) => void;
  searchTouched: boolean;
  location: { latitude: number; longitude: number } | null;
  getGeolocation: () => void;
  mapContainerRef: React.RefObject<HTMLDivElement>;
  handleLocateUser: () => void;
  selectCourse: boolean;
  customSearchCaption?: string;
  noTabs?: boolean;
  backHandler?: () => void;
}

export const SearchUI: React.FC<SearchUIProps> = ({
  showMap,
  setShowMap,
  searchRef,
  inputValue,
  setInputValue,
  segment,
  handleSegmentChange,
  isSearching,
  courses,
  playedCourses,
  favoriteCourses,
  handleSelectCourse,
  searchTouched,
  location,
  getGeolocation,
  mapContainerRef,
  handleLocateUser,
  selectCourse,
  customSearchCaption,
  backHandler,
  noTabs
}) => {
  const { t } = useTranslation('MGP');

  return (
    <>
      <IonHeader className={'ion-no-border shadow-md' + (showMap ? 'absolute top-0 left-0 right-0 z-10' : '')}>
        {!showMap && (
          <IonToolbar color="light">
            <IonButtons slot="start">
              {backHandler ? (
                <IonButton onClick={backHandler}>
                  <IonIcon slot="icon-only" icon={arrowBackOutline} />
                </IonButton>
              ) : (
                <IonBackButton defaultHref='' text='' icon={arrowBackOutline} />
              )}
            </IonButtons>
            <IonTitle>{t('select_golf_course')}</IonTitle>
          </IonToolbar>
        )}
        <IonToolbar color="light">
          <IonSearchbar
            color="secondary"
            ref={searchRef}
            showCancelButton={showMap ? 'never' : 'never'}
            onIonCancel={() => setInputValue('')}
            cancelButtonText={t('cancel')}
            autoFocus
            autocomplete='off'
            name="selectedCourse"
            placeholder={customSearchCaption || t('search_golf_course_placeholder')}
            onIonInput={(e) => setInputValue(e.detail.value || '')}
            value={inputValue}
            className='p-0 mt-2'
            style={showMap ? {
              '--background': 'rgba(255,255,255,1.0)',
            } : {}}
          />
        </IonToolbar>
        {!showMap && !noTabs && (
          <IonToolbar color="light" className='ion-no-padding'>
            <IonSegment color="primary" mode="md" value={segment} onIonChange={(e) => handleSegmentChange(e.detail.value as any)}>
              <IonSegmentButton value="nearest">
                {t('nearest')}
              </IonSegmentButton>
              <IonSegmentButton value="favorites">
                {t('favorites')}
              </IonSegmentButton>
            </IonSegment>
          </IonToolbar>
        )}
      </IonHeader>
      <IonContent color="light" className="h-full">
        {showMap ? (
          <>
            <ClubSearchMap
              mapContainerRef={mapContainerRef}
              onCourseSelect={handleSelectCourse}
              handleLocateUser={handleLocateUser}
              location={location}
              className="size-full"
            />
            <IonFab color='secondary' className='mb-safe' vertical="bottom" horizontal="center" slot="fixed">
              <IonButton shape='round' className='shadow-md ring-2 rounded-full ring-input/50' color="dark" onClick={() => setShowMap(false)}>
                <IonIcon className='mr-2' icon={listOutline} />
                {t('list')}
              </IonButton>
            </IonFab>
          </>
        ) : (
          <>
            <IonList lines='none' className="flex-1 bg-transparent overflow-y-auto">
              {isSearching ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="size-6 animate-spin text-muted-foreground" />
                </div>
              ) : courses.length > 0 ? (
                courses.map((course) => (
                  <FacilityListItem
                    key={course.id}
                    course={course}
                    onSelect={() => handleSelectCourse(course)}
                    showCourseCount={selectCourse}
                    showDistance={true}
                    showChevron={selectCourse && course.courses && course.courses.length > 1}
                  />
                ))
              ) : (
                <>
                  {!isSearching && segment === 'nearest' && inputValue.length === 0 && location == null && (
                    <div hidden={isSearching || location != null} className="flex flex-col justify-center items-center mt-8 gap-4 py-6">
                      <div className="flex flex-col items-center justify-center">
                        <img src="/assets/images/NoGPS.svg" alt="Location services disabled" />
                        <p className="max-w-sm text-center my-4 px-4 text-muted-foreground">
                          {t('enable_location_tip')}
                        </p>
                        <IonButton
                          shape='round'
                          onClick={getGeolocation}
                        >
                          {t('enable_location')}
                        </IonButton>
                      </div>
                    </div>
                  )}

                  {!isSearching && segment === 'playedCourses' && inputValue.length === 0 && playedCourses.length === 0 && (
                    <div className="flex flex-col justify-center items-center mt-8 gap-4 py-6">
                      <div className="flex flex-col items-center justify-center">
                        <img src="/assets/images/NoHistory.svg" alt="No History" />
                        <p className="max-w-sm text-center my-4 px-4 text-muted-foreground">
                          {t('no_recent_courses')}
                        </p>
                      </div>
                    </div>
                  )}

                  {!isSearching && segment === 'favorites' && inputValue.length === 0 && favoriteCourses.length === 0 && (
                    <div className="flex flex-col justify-center items-center mt-8 gap-4 py-6">
                      <div className="flex flex-col items-center justify-center">
                        <img src="/assets/images/NoGPS.svg" alt="No Favorites" />
                        <p className="max-w-sm text-center my-4 px-4 text-muted-foreground">
                          {t('no_favorites_clubs_caption')}
                        </p>
                      </div>
                    </div>
                  )}

                  {inputValue.length > 0 && (
                    <div className="flex text-center items-center justify-center py-6" hidden={!searchTouched || isSearching || inputValue.length === 0}>
                      {t('no_results')}
                    </div>
                  )}
                </>
              )}
            </IonList>
            <IonFab color='secondary' className='mb-safe' vertical="bottom" horizontal="center" slot="fixed">
              <IonButton className='shadow-md ring-2 rounded-full ring-input/50' shape='round' color="dark" onClick={() => setShowMap(!showMap)}>
                <IonIcon className='mr-2' icon={mapOutline} />
                {t('map')}
              </IonButton>
            </IonFab>
          </>
        )}
      </IonContent>
    </>
  );
};
